import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Gift,
  Plus,
  RefreshCw,
  Search,
  Copy,
  Eye,
  Send,
  Ban,
  ChevronDown,
  AlertTriangle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import {
  GIFT_REASONS,
  GIFT_STATUS_LABELS,
  formatIls,
  GiftAdminError,
  giftAdmin,

  type GiftCardRow,
  type GiftCardSettings,
  type GiftEvent,
  type GiftHealth,
  type GiftTransaction,
} from '@/lib/giftCards';
import { minExpiryIso, newIdempotencyKey, validateCreateCard } from '@/lib/giftValidation';
import GiftBulkEditor from '@/components/admin/GiftBulkEditor';
import GiftSettingsPanel from '@/components/admin/GiftSettingsPanel';
import GiftPurchaseAdmin from '@/components/admin/GiftPurchaseAdmin';
import { GiftDebitForm } from '@/components/admin/GiftDebitForm';
import { GiftCreditForm } from '@/components/admin/GiftCreditForm';
import GiftSystemStatus from '@/components/admin/GiftSystemStatus';

type Tab = 'cards' | 'purchases' | 'campaigns' | 'settings';

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'הכול' },
  { value: 'sent', label: 'נשלחו' },
  { value: 'scheduled', label: 'מתוזמנים' },
  { value: 'opened', label: 'נצפו' },
  { value: 'active', label: 'פעילים' },
  { value: 'partially_used', label: 'נוצלו חלקית' },
  { value: 'depleted', label: 'נוצלו' },
  { value: 'expired', label: 'פג תוקף' },
  { value: 'revoked', label: 'בוטלו' },
  { value: 'failed', label: 'נכשלו' },
];

export default function AdminGiftCards() {
  const [tab, setTab] = useState<Tab>('cards');
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState<GiftHealth | null>(null);
  const [settings, setSettings] = useState<GiftCardSettings | null>(null);

  const [cards, setCards] = useState<GiftCardRow[]>([]);
  const [totals, setTotals] = useState({ issued: 0, remaining: 0, used: 0, activeCount: 0, count: 0 });
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [listBusy, setListBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const canIssue = !!health?.shopifyGiftCards.read && !!health?.encryption;

  const loadMeta = async () => {
    try {
      const res = await giftAdmin<{ settings: GiftCardSettings; health: GiftHealth }>('settings.get');
      setSettings(res.settings);
      setHealth(res.health);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'טעינת ההגדרות נכשלה');
    }
  };

  const loadCards = async () => {
    setListBusy(true);
    try {
      const res = await giftAdmin<{ cards: GiftCardRow[]; totals: typeof totals }>('cards.list', {
        status,
        search,
      });
      setCards(res.cards);
      setTotals(res.totals);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'טעינת השוברים נכשלה');
    } finally {
      setListBusy(false);
    }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([loadMeta(), loadCards()]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading) return;
    const t = setTimeout(loadCards, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, search]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const res = await giftAdmin<{ synced: number; delivered: number; deliveryFailed: number; emailConfigured: boolean }>(
        'sync.run',
      );
      toast.success(
        `סונכרנו ${res.synced} שוברים${res.delivered ? `, נשלחו ${res.delivered}` : ''}${
          res.deliveryFailed ? `, נכשלו ${res.deliveryFailed}` : ''
        }`,
      );
      loadCards();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'סנכרון נכשל');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-16 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> טוען שוברי מתנה…
      </div>
    );
  }

  return (
    <div dir="rtl" className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Gift className="h-6 w-6" /> שובר מתנה
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            הנפקה, שליחה ומעקב אחרי שוברי מתנה. היתרה והמימוש מנוהלים בשופיפיי.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="gap-2" asChild>
            <a href="/admin/gift-cards/preview">תצוגה מקדימה</a>
          </Button>
          <Button variant="outline" size="sm" onClick={runSync} disabled={syncing} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} /> סנכרון יתרות
          </Button>
          <Button size="sm" className="gap-2" onClick={() => setCreateOpen((v) => !v)} disabled={!canIssue}>
            <Plus className="h-4 w-4" /> שובר חדש
          </Button>
        </div>
      </header>

      <HealthBanner health={health} />
      <GiftSystemStatus health={health} />

      <div className="flex gap-1 border-b">
        {(
          [
            ['cards', 'שוברים'],
            ['purchases', 'רכישות מהאתר'],
            ['campaigns', 'שליחה מרוכזת'],
            ['settings', 'עיצוב והגדרות'],
          ] as Array<[Tab, string]>
        ).map(([value, label]) => (
          <button
            key={value}
            onClick={() => setTab(value)}
            className={`px-4 py-2 text-sm rounded-t-md transition ${
              tab === value ? 'bg-white border border-b-white -mb-px font-medium' : 'text-muted-foreground hover:bg-neutral-100'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'cards' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="סה״כ הונפק" value={formatIls(totals.issued)} />
            <StatCard label="יתרה פתוחה" value={formatIls(totals.remaining)} />
            <StatCard label="נוצל" value={formatIls(totals.used)} />
            <StatCard label="שוברים פעילים" value={String(totals.activeCount)} />
          </div>

          {createOpen && settings && (
            <CreateCardForm
              settings={settings}
              onCancel={() => setCreateOpen(false)}
              onCreated={() => {
                setCreateOpen(false);
                loadCards();
              }}
              emailConfigured={!!health?.email}
            />
          )}

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="חיפוש לפי שם, מייל, טלפון, 4 ספרות או קישור אישי"
                className="pe-9"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setStatus(f.value)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition ${
                    status === f.value ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white hover:bg-neutral-100'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {listBusy && <div className="text-xs text-muted-foreground">מרענן…</div>}

          {cards.length === 0 ? (
            <div className="border rounded-xl bg-white p-10 text-center">
              <Gift className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <div className="font-medium">אין שוברים להצגה</div>
              <p className="text-sm text-muted-foreground mt-1">
                {status === 'all' ? 'הנפיקו שובר ראשון כדי להתחיל.' : 'נסו לשנות את הסינון או החיפוש.'}
              </p>
            </div>
          ) : (
            <div className="border rounded-xl bg-white divide-y overflow-hidden">
              {cards.map((card) => (
                <CardRow
                  key={card.id}
                  card={card}
                  expanded={expandedId === card.id}
                  onToggle={() => setExpandedId(expandedId === card.id ? null : card.id)}
                  onChanged={loadCards}
                  emailConfigured={!!health?.email}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'purchases' && <GiftPurchaseAdmin />}

      {tab === 'campaigns' && settings && (
        <GiftBulkEditor settings={settings} canIssue={canIssue} emailConfigured={!!health?.email} onDone={loadCards} />
      )}

      {tab === 'settings' && settings && (
        <GiftSettingsPanel settings={settings} onSaved={(s) => setSettings(s)} health={health} />
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="border rounded-xl bg-white p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function HealthBanner({ health }: { health: GiftHealth | null }) {
  if (!health) return null;
  const problems: string[] = [];
  if (!health.shopifyGiftCards.read) {
    problems.push(
      'הרשאות שוברי מתנה בשופיפיי חסרות. יש להוסיף לאפליקציה המותאמת את ההרשאות read_gift_cards ו-write_gift_cards ולהתקין מחדש. עד אז ניתן לערוך עיצוב והגדרות ולהכין רשימות, אך לא להנפיק.',
    );
  }
  if (!health.shopifyGiftCards.write_transactions) {
    problems.push(
      'ההרשאות read_gift_card_transactions ו-write_gift_card_transactions חסרות. הבקאנד לחיוב/זיכוי ידני מוכן, אך פעולות כספיות יידחו עד להוספת ההרשאות והתקנה מחדש של האפליקציה המותאמת.',
    );
  }
  if (health.publicSiteUrl === false) {
    problems.push(
      'הסוד PUBLIC_SITE_URL חסר או לא תקין. יצירת קישורים אישיים ושליחת מיילים נחסמות עד להגדרתו — לא מונחת כתובת ברירת מחדל.',
    );
  }
  if (health.pepper === false) {
    problems.push(
      'הסוד GIFT_CARD_SECURITY_PEPPER חסר. עמודי השובר הציבוריים (/gift/:token) חסומים עד להוספתו בהגדרות הפרויקט → Secrets.',
    );
  }
  if (!health.encryption) {
    problems.push('הסוד GIFT_CARD_ENCRYPTION_KEY חסר. ללא הצפנה לא ניתן להנפיק שוברים — יש להוסיף אותו בהגדרות הפרויקט → Secrets.');
  }
  if (health.purchase && health.purchase.publicSaleEnabled && !health.purchase.ready) {
    problems.push(
      `רכישת שוברים באתר חסומה עד להשלמת ההגדרות: ${health.purchase.blockers.join(' · ')}. יש להגדיר ב-Shopify webhook לנושא orders/paid לכתובת פונקציית gift-card-webhook ולשמור את אותו סוד בהגדרות הפרויקט → Secrets בשם SHOPIFY_WEBHOOK_SECRET (הערך עצמו אינו מוצג כאן).`,
    );
  }
  if (!health.email) {
    problems.push('שליחה לא מוגדרת: חסרים RESEND_API_KEY ו-GIFT_CARD_FROM_EMAIL. אפשר להנפיק שובר ולהעתיק קישור אישי ידנית.');
  }

  if (!problems.length) {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
        <span>המערכת מוגדרת במלואה: שופיפיי, הצפנה ושליחת מייל.</span>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-1.5">
      {problems.map((p, i) => (
        <div key={i} className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{p}</span>
        </div>
      ))}
      {health.shopifyGiftCards.message && (
        <div className="text-xs opacity-70 ps-6">פירוט: {health.shopifyGiftCards.message}</div>
      )}
    </div>
  );
}

function CreateCardForm({
  settings,
  onCancel,
  onCreated,
  emailConfigured,
}: {
  settings: GiftCardSettings;
  onCancel: () => void;
  onCreated: () => void;
  emailConfigured: boolean;
}) {
  const [amount, setAmount] = useState(String(settings.presets_ils?.[0] ?? 250));
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [senderName, setSenderName] = useState(settings.sender_name ?? '');
  const [message, setMessage] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [occasion, setOccasion] = useState('');
  const [reason, setReason] = useState('complimentary');
  const [deliverAt, setDeliverAt] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [delivery, setDelivery] = useState<'email' | 'link'>(emailConfigured ? 'email' : 'link');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [emailFailed, setEmailFailed] = useState(false);
  const [confirmNewIntent, setConfirmNewIntent] = useState(false);
  // Safe-retry banner: set when the server outcome is uncertain / in progress.
  const [pending, setPending] = useState<{ kind: 'in_progress' | 'needs_reconciliation' | 'conflict'; message: string } | null>(
    null,
  );
  // One key per confirmed intent: a retry after a network error can never
  // create a second gift card in Shopify.
  const [idemKey, setIdemKey] = useState(() => newIdempotencyKey('issue'));

  const check = validateCreateCard({
    amount,
    recipient_name: name,
    recipient_email: email,
    recipient_phone: phone,
    delivery,
    expires_on: expiresOn,
  });

  const submit = async () => {
    if (!check.ok) return toast.error(check.error);
    if (busy) return;
    // A reconciliation-pending or conflicting operation must never be re-sent.
    if (pending && pending.kind !== 'in_progress') return;
    setBusy(true);
    try {
      const res = await giftAdmin<{ link: string; emailStatus: string; gift_card_id: string; replay?: boolean }>(
        'card.create',
        {
          amount: Number(amount),
          recipient_name: name.trim(),
          recipient_email: email.trim() || null,
          recipient_phone: phone.trim() || null,
          sender_name: senderName.trim() || null,
          message: message.trim() || null,
          internal_note: internalNote.trim() || null,
          occasion: occasion || null,
          issue_reason: reason,
          deliver_at: deliverAt ? new Date(deliverAt).toISOString() : null,
          expires_on: expiresOn || null,
          send: delivery === 'email',
          idempotency_key: idemKey,
        },
      );
      setLink(res.link);
      setCreatedId(res.gift_card_id ?? null);
      setEmailFailed(res.emailStatus === 'failed');
      setConfirming(false);
      setPending(null);
      if (res.replay) toast.info('השובר כבר הונפק — מוצג הקישור הקיים');
      else if (res.emailStatus === 'sent') toast.success('השובר הונפק ונשלח במייל');
      else if (res.emailStatus === 'not_configured')
        toast.success('השובר הונפק. שליחה לא מוגדרת — העתיקו את הקישור האישי.');
      else if (res.emailStatus === 'failed')
        toast.error('השובר הונפק אך שליחת המייל נכשלה — השובר תקף, ניתן להעתיק קישור או לנסות שליחה שוב');
      else toast.success('השובר הונפק');
      // The financial card exists now; a further submit is a new operation.
      setIdemKey(newIdempotencyKey('issue'));
      onCreated();
    } catch (e) {
      const err = e instanceof GiftAdminError ? e : null;
      const msg = e instanceof Error ? e.message : 'ההנפקה נכשלה';
      setConfirming(false);
      if (err?.keepKey) {
        // Keep the SAME idempotency key — the operation may exist server-side.
        const kind =
          err.status === 'needs_reconciliation' || err.status === 'conflict' ? err.status : 'in_progress';
        setPending({ kind, message: msg });
        setConfirmNewIntent(false);
        toast.error(msg);
      } else {
        // Definitive failure: nothing was created, so a new key is required.
        setPending(null);
        setIdemKey(newIdempotencyKey('issue'));
        toast.error(msg);
      }
      onCreated();
    } finally {
      setBusy(false);
    }
  };



  return (
    <div className="border rounded-xl bg-white p-4 space-y-4">
      <div className="font-medium">הנפקת שובר בודד</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="סכום (₪)">
          <div className="space-y-2">
            <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="numeric" />
            <div className="flex flex-wrap gap-1">
              {(settings.presets_ils ?? []).map((p) => (
                <button
                  key={String(p)}
                  onClick={() => setAmount(String(p))}
                  className="px-2.5 py-1 rounded-full border text-xs hover:bg-neutral-100"
                >
                  {formatIls(Number(p))}
                </button>
              ))}
            </div>
          </div>
        </Field>
        <Field label="שם המקבל/ת *">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="מייל המקבל/ת">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" dir="ltr" />
        </Field>
        <Field label="טלפון">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} dir="ltr" />
        </Field>
        <Field label="שם השולח/ת">
          <Input value={senderName} onChange={(e) => setSenderName(e.target.value)} />
        </Field>
        <Field label="אירוע">
          <select
            className="w-full h-10 rounded-md border bg-white px-3 text-sm"
            value={occasion}
            onChange={(e) => setOccasion(e.target.value)}
          >
            <option value="">ללא</option>
            {(settings.occasions ?? []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </Field>
        <Field label="סוג הנפקה">
          <select
            className="w-full h-10 rounded-md border bg-white px-3 text-sm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          >
            {Object.entries(GIFT_REASONS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field label="שליחה מתוזמנת (אופציונלי)">
          <Input type="datetime-local" value={deliverAt} onChange={(e) => setDeliverAt(e.target.value)} />
        </Field>
        <Field label="תאריך תפוגה (אופציונלי)">
          <Input
            type="date"
            value={expiresOn}
            min={minExpiryIso()}
            onChange={(e) => setExpiresOn(e.target.value)}
            dir="ltr"
            className="text-right"
          />
          <div className="text-[11px] text-muted-foreground mt-1">
            {expiresOn ? `בתוקף עד ${expiresOn}` : 'ריק = ללא תאריך תפוגה'}
          </div>
        </Field>
      </div>
      <Field label="הקדשה">
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} maxLength={400} />
      </Field>
      <Field label="הערה פנימית (לא נשלחת למקבל/ת)">
        <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} rows={2} maxLength={400} />
      </Field>
      <Field label="אופן מסירה">
        <div className="flex flex-wrap gap-2">
          {([
            { value: 'email' as const, label: 'שליחה במייל' },
            { value: 'link' as const, label: 'הנפקה והעתקת קישור' },
          ]).map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                setDelivery(opt.value);
                setConfirming(false);
              }}
              disabled={opt.value === 'email' && !emailConfigured}
              className={`px-3 py-1.5 rounded-full border text-xs transition disabled:opacity-40 ${
                delivery === opt.value ? 'bg-neutral-900 text-white border-neutral-900' : 'hover:bg-neutral-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </Field>
      {!emailConfigured && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
          שליחה לא מוגדרת — לאחר ההנפקה יופיע כאן קישור אישי להעברה ידנית.
        </div>
      )}
      {link && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 rounded-md border bg-neutral-50 p-2 text-xs">
            <span dir="ltr" className="truncate flex-1">
              {link}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              onClick={() => {
                navigator.clipboard.writeText(link);
                toast.success('הקישור הועתק');
              }}
            >
              <Copy className="h-3.5 w-3.5" /> העתקה
            </Button>
          </div>
          {emailFailed && createdId && emailConfigured && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await giftAdmin('card.resend', { id: createdId });
                  setEmailFailed(false);
                  toast.success('נשלח מחדש');
                } catch (e) {
                  toast.error(e instanceof Error ? e.message : 'השליחה נכשלה');
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Send className="h-3.5 w-3.5" /> ניסיון שליחה חוזר
            </Button>
          )}
        </div>
      )}

      {pending && (
        <div
          className={`rounded-md border p-3 text-xs space-y-2 ${
            pending.kind === 'in_progress'
              ? 'bg-amber-50 border-amber-200 text-amber-900'
              : 'bg-rose-50 border-rose-200 text-rose-900'
          }`}
        >
          <div className="font-medium">
            {pending.kind === 'in_progress'
              ? 'ההנפקה בתהליך'
              : pending.kind === 'needs_reconciliation'
                ? 'נדרשת בדיקה ידנית בשופיפיי'
                : 'נתוני הבקשה שונו'}
          </div>
          <p>{pending.message}</p>
          <p className="opacity-80">
            {pending.kind === 'in_progress'
              ? 'ניתן לנסות שוב את אותה הבקשה — לא ייווצר שובר כפול.'
              : 'אין לנסות שוב עם אותה בקשה. יש לבדוק את רשימת השוברים ולפתוח הנפקה חדשה במידת הצורך.'}
          </p>
          <div className="flex gap-2">
            {pending.kind === 'in_progress' ? (
              <Button size="sm" variant="outline" onClick={submit} disabled={busy} className="gap-1">
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} בדיקה/ניסיון חוזר
              </Button>
            ) : (
              <div className="space-y-2">
                {!confirmNewIntent ? (
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirmNewIntent(true)}>פתיחת הנפקה חדשה</Button>
                ) : (
                  <div className="rounded-md border border-rose-300 p-2 space-y-2">
                    <p>פעולה זו יוצרת כוונת הנפקה חדשה עם מפתח חדש. יש לבצע אותה רק לאחר בדיקה שהבקשה הקודמת לא צריכה להמשיך.</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="destructive" disabled={busy} onClick={() => { setPending(null); setConfirmNewIntent(false); setIdemKey(newIdempotencyKey('issue')); }}>אישור והתחלת הנפקה חדשה</Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmNewIntent(false)}>ביטול</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {confirming ? (
        <div className="rounded-md border bg-white p-3 space-y-2 text-sm">
          <div className="font-medium">אישור הנפקה</div>
          <p className="text-muted-foreground text-xs">
            ייווצר שובר מתנה אמיתי בשופיפיי בסך <strong>{formatIls(Number(amount))}</strong> עבור{' '}
            <strong>{name.trim()}</strong>
            {delivery === 'email' ? ` וישלח למייל ${email.trim()}` : ' ללא שליחה — הקישור יוצג להעתקה'}.
          </p>
          <div className="flex gap-2">
            <Button onClick={submit} disabled={busy} className="gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} אישור והנפקה
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>
              חזרה לעריכה
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button
            onClick={() => (check.ok ? setConfirming(true) : toast.error(check.error))}
            disabled={busy || (pending !== null && pending.kind !== 'in_progress')}
          >
            המשך לאישור
          </Button>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            ביטול
          </Button>
        </div>
      )}

    </div>

  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'failed' || status === 'revoked' || status === 'expired'
      ? 'bg-rose-50 text-rose-700 border-rose-200'
      : status === 'depleted'
        ? 'bg-neutral-100 text-neutral-600 border-neutral-200'
        : status === 'scheduled' || status === 'pending_payment'
          ? 'bg-amber-50 text-amber-800 border-amber-200'
          : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return (
    <span className={`px-2 py-0.5 rounded-full border text-[11px] whitespace-nowrap ${tone}`}>
      {GIFT_STATUS_LABELS[status] ?? status}
    </span>
  );
}

function CardRow({
  card,
  expanded,
  onToggle,
  onChanged,
  emailConfigured,
}: {
  card: GiftCardRow;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
  emailConfigured: boolean;
}) {
  const [events, setEvents] = useState<GiftEvent[] | null>(null);
  const [transactions, setTransactions] = useState<GiftTransaction[]>([]);
  const [otpChallenges, setOtpChallenges] = useState<OtpChallengeRow[]>([]);
  const [detail, setDetail] = useState<GiftCardRow | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // The balance shown in the clinic view is always refreshed from Shopify.
  const loadDetail = async () => {
    setDetailLoading(true);
    try {
      const res = await giftAdmin<{
        card: GiftCardRow;
        events: GiftEvent[];
        transactions: GiftTransaction[];
        otpChallenges?: OtpChallengeRow[];
        refreshError: string | null;
      }>('card.detail', { id: card.id });
      setDetail(res.card);
      setEvents(res.events ?? []);
      setTransactions(res.transactions ?? []);
      setOtpChallenges(res.otpChallenges ?? []);
      setRefreshError(res.refreshError ?? null);

    } catch (e) {
      setEvents([]);
      setRefreshError(e instanceof Error ? e.message : 'טעינת השובר נכשלה');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (!expanded || events) return;
    void loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const view = detail ?? card;


  const act = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'הפעולה נכשלה');
    } finally {
      setBusy(null);
    }
  };

  const usedAmount = Math.max(0, Number(view.initial_amount) - Number(view.balance_amount ?? view.initial_amount));

  return (
    <div>
      <button onClick={onToggle} className="w-full text-start px-4 py-3 hover:bg-neutral-50 transition flex items-center gap-3">
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition ${expanded ? 'rotate-180' : ''}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium truncate">{card.recipient_name}</span>
            <StatusPill status={card.status} />
            {card.last_characters && (
              <span dir="ltr" className="text-[11px] text-muted-foreground">
                •••• {card.last_characters}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground truncate mt-0.5">
            {card.recipient_email ?? card.recipient_phone ?? 'ללא פרטי קשר'} ·{' '}
            {new Date(card.created_at).toLocaleDateString('he-IL')}
          </div>
        </div>
        <div className="text-end">
          <div className="font-semibold">{formatIls(Number(card.initial_amount))}</div>
          <div className="text-[11px] text-muted-foreground">יתרה {formatIls(card.balance_amount === null ? null : Number(card.balance_amount))}</div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 bg-neutral-50/60 space-y-4">
          <RecipientEmailPanel
            cardId={card.id}
            email={view.recipient_email}
            otpChallenges={otpChallenges}
            onSaved={() => {
              void loadDetail();
              onChanged();
            }}
          />
          <div className="flex flex-wrap gap-2">

            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={busy !== null}
              onClick={() =>
                act('link', async () => {
                  const res = await giftAdmin<{ link: string }>('card.link', { id: card.id });
                  await navigator.clipboard.writeText(res.link);
                  toast.success('הקישור האישי הועתק');
                })
              }
            >
              <Copy className="h-3.5 w-3.5" /> העתקת קישור אישי
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={busy !== null}
              onClick={() =>
                act('reveal', async () => {
                  const res = await giftAdmin<{ code: string }>('card.reveal', { id: card.id });
                  setCode(res.code);
                })
              }
            >
              <Eye className="h-3.5 w-3.5" /> חשיפת קוד
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={busy !== null || !card.recipient_email || !emailConfigured}
              title={!emailConfigured ? 'שליחה לא מוגדרת' : undefined}
              onClick={() =>
                act('resend', async () => {
                  await giftAdmin('card.resend', { id: card.id });
                  toast.success('נשלח מחדש');
                  onChanged();
                })
              }
            >
              <Send className="h-3.5 w-3.5" /> שליחה מחדש
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={busy !== null}
              onClick={() =>
                act('sync', async () => {
                  await giftAdmin('card.sync_one', { id: card.id });
                  toast.success('סונכרן');
                  onChanged();
                })
              }
            >
              <RefreshCw className="h-3.5 w-3.5" /> סנכרון יתרה
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1 text-rose-700 hover:text-rose-800"
              disabled={busy !== null || card.status === 'revoked'}
              onClick={() =>
                act('revoke', async () => {
                  if (!confirm('לבטל את השובר? הפעולה משביתה אותו בשופיפיי.')) return;
                  await giftAdmin('card.revoke', { id: card.id });
                  toast.success('השובר בוטל');
                  onChanged();
                })
              }
            >
              <Ban className="h-3.5 w-3.5" /> ביטול שובר
            </Button>
          </div>

          {code && (
            <div className="rounded-md border bg-white p-3 text-sm">
              <div className="text-xs text-muted-foreground mb-1">קוד השובר</div>
              <div dir="ltr" className="font-mono tracking-widest">
                {code}
              </div>
            </div>
          )}

          {refreshError && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              רענון היתרה משופיפיי נכשל: {refreshError}. היתרה המוצגת עלולה להיות לא מעודכנת.
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Meta label="הונפק" value={formatIls(Number(view.initial_amount))} />
            <Meta
              label={detailLoading ? 'יתרה (מתעדכן…)' : 'יתרה בשופיפיי'}
              value={formatIls(view.balance_amount === null ? null : Number(view.balance_amount))}
            />
            <Meta label="נוצל" value={formatIls(usedAmount)} />
            <Meta label="תוקף" value={view.expires_on ?? 'ללא'} />
            <Meta label="מקור" value={view.source === 'purchase' ? 'רכישה באתר' : GIFT_REASONS[view.issue_reason ?? ''] ?? 'הנפקה ידנית'} />
            <Meta label="נשלח" value={view.sent_at ? new Date(view.sent_at).toLocaleString('he-IL') : '—'} />
            <Meta label="נצפה" value={view.opened_at ? new Date(view.opened_at).toLocaleString('he-IL') : '—'} />
            <Meta label="סונכרן" value={view.balance_synced_at ? new Date(view.balance_synced_at).toLocaleString('he-IL') : '—'} />
          </div>

          {view.message && (
            <div className="rounded-md border bg-white p-3 text-sm">
              <div className="text-xs text-muted-foreground mb-1">הקדשה</div>
              {view.message}
            </div>
          )}

          <div className="grid gap-3 lg:grid-cols-2">
            <GiftDebitForm
              cardId={view.id}
              balance={view.balance_amount === null ? null : Number(view.balance_amount)}
              status={view.status}
              currency={view.currency}
              onDone={async () => {
                await loadDetail();
                onChanged();
              }}
            />
            <GiftCreditForm
              cardId={view.id}
              currency={view.currency}
              transactions={transactions}
              onDone={async () => {
                await loadDetail();
                onChanged();
              }}
            />
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1">פעולות כספיות</div>
            {transactions.length === 0 ? (
              <div className="text-xs text-muted-foreground">אין פעולות</div>
            ) : (
              <div className="rounded-md border bg-white divide-y text-xs">
                {transactions.map((t) => (
                  <div key={t.id} className="px-3 py-2 flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2">
                      <span className={t.type === 'debit' ? 'text-rose-700' : 'text-emerald-700'}>
                        {t.type === 'debit' ? 'חיוב' : 'זיכוי'} {formatIls(Number(t.amount))}
                      </span>
                      <span className="text-muted-foreground">
                        {t.status === 'succeeded'
                          ? 'הושלם'
                          : t.status === 'processing'
                            ? 'בתהליך'
                            : t.status === 'needs_reconciliation'
                              ? 'דורש בירור'
                              : 'נכשל'}
                      </span>
                      {t.service_reference && <span className="text-muted-foreground">· {t.service_reference}</span>}
                    </span>
                    <span className="text-muted-foreground">{new Date(t.created_at).toLocaleString('he-IL')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1">היסטוריה</div>
            {events === null ? (
              <div className="text-xs text-muted-foreground">טוען…</div>
            ) : events.length === 0 ? (
              <div className="text-xs text-muted-foreground">אין רשומות</div>
            ) : (
              <div className="rounded-md border bg-white divide-y text-xs">
                {events.map((e) => (
                  <div key={e.id} className="px-3 py-2 flex items-center justify-between gap-3">
                    <span>{e.type}</span>
                    <span className="text-muted-foreground">{new Date(e.created_at).toLocaleString('he-IL')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-white p-2.5">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}

/** Delivery/security state of the recipient OTP flow — never digests or IPs. */
interface OtpChallengeRow {
  created_at: string;
  delivery_state: string | null;
  delivery_error_class: string | null;
  attempts: number;
  max_attempts: number;
  expires_at: string;
  locked_until: string | null;
  verified_at: string | null;
}

const OTP_DELIVERY_LABELS: Record<string, string> = {
  pending: 'ממתין',
  sent: 'נשלח',
  failed: 'שליחה נכשלה',
  no_recipient: 'אין כתובת מייל',
  not_configured: 'שליחה לא מוגדרת',
};

/**
 * Audited correction of the recipient email + a read-only summary of recent
 * OTP requests. No Shopify mutation, no resend, no secret material displayed.
 */
function RecipientEmailPanel({
  cardId,
  email,
  otpChallenges,
  onSaved,
}: {
  cardId: string;
  email: string | null;
  otpChallenges: OtpChallengeRow[];
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(email ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const next = value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(next)) {
      toast.error('כתובת מייל אינה תקינה');
      return;
    }
    if (!window.confirm(`לעדכן את כתובת המייל של מקבל השובר ל: ${next}?`)) return;
    setSaving(true);
    try {
      await giftAdmin('card.set_email', { id: cardId, email: next, confirm: true });
      toast.success('כתובת המייל עודכנה');
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'העדכון נכשל');
    } finally {
      setSaving(false);
    }
  };

  const latest = otpChallenges[0];

  return (
    <div className="rounded-lg border bg-white p-3 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs">
          <span className="text-muted-foreground">כתובת מייל למקבל: </span>
          <span dir="ltr">{email ?? '—'}</span>
        </div>
        {!editing && (
          <Button size="sm" variant="outline" onClick={() => { setValue(email ?? ''); setEditing(true); }}>
            עדכון כתובת מייל
          </Button>
        )}
      </div>

      {editing && (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            dir="ltr"
            type="email"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="name@example.com"
            className="h-8 max-w-xs text-xs"
          />
          <Button size="sm" onClick={save} disabled={saving}>
            שמירה
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
            ביטול
          </Button>
        </div>
      )}

      <div className="text-[11px] text-muted-foreground">
        {otpChallenges.length === 0 ? (
          'לא נשלחו קודי אימות לצפייה בקוד השובר.'
        ) : (
          <>
            <div>
              בקשת אימות אחרונה: {new Date(latest.created_at).toLocaleString('he-IL')} ·{' '}
              {OTP_DELIVERY_LABELS[latest.delivery_state ?? 'pending'] ?? latest.delivery_state} ·{' '}
              {latest.verified_at ? 'אומת' : `נסיונות ${latest.attempts}/${latest.max_attempts}`}
              {latest.locked_until && new Date(latest.locked_until) > new Date() ? ' · נחסם זמנית' : ''}
            </div>
            <div>סה״כ בקשות אחרונות: {otpChallenges.length}</div>
          </>
        )}
      </div>
    </div>
  );
}
